import React, { useState } from "react";
import { Sun, Moon } from "lucide-react";

function LandingPage({ onGetStarted }) {
  const [theme, setTheme] = useState("dark"); // 'dark' | 'light'

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Define colors based on theme
  const t =
    theme === "dark"
      ? {
          bg: "linear-gradient(135deg, #020617 0%, #020617 40%, #0b1120 100%)",
          navBg: "rgba(15, 23, 42, 0.98)",
          text: "#f9fafb",
          textSec: "#9ca3af",
          border: "rgba(148, 163, 184, 0.2)",
          accent: "#0ea5e9",
          cardBg: "rgba(15, 23, 42, 0.6)",
          cardBorder: "rgba(148, 163, 184, 0.2)",
          rightCardBg: "#f9fafb",
          rightCardText: "#020617",
          badgeBg: "rgba(15, 23, 42, 0.8)",
          badgeText: "#cbd5f5",
          flowStepBg:
            "radial-gradient(circle at top left, rgba(56, 189, 248, 0.06), transparent 60%)",
          flowBorder: "rgba(148, 163, 184, 0.2)",
          flowTitle: "#cbd5f5",
          flowStepDot: "#020617",
        }
      : {
          bg: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 40%, #dbeafe 100%)",
          navBg: "rgba(249, 250, 251, 0.9)",
          text: "#020617",
          textSec: "#4b5563",
          border: "rgba(148, 163, 184, 0.3)",
          accent: "#0284c7",
          cardBg: "#ffffff",
          cardBorder: "rgba(148, 163, 184, 0.3)",
          rightCardBg: "#ffffff",
          rightCardText: "#020617",
          badgeBg: "rgba(15, 23, 42, 0.05)",
          badgeText: "#1d4ed8",
          flowStepBg:
            "radial-gradient(circle at top left, rgba(37, 99, 235, 0.06), #f9fafb 70%)",
          flowBorder: "rgba(148, 163, 184, 0.4)",
          flowTitle: "#1f2937",
          flowStepDot: "#ffffff",
        };

  return (
    <>
      <style>{`
        /* Global Reset & Base */
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: 'Inter', system-ui, sans-serif; }

        .landing-page {
          min-height: 100vh; /* FIX: Allows scrolling on zoom */
          background: ${t.bg};
          color: ${t.text};
          transition: background 0.3s ease, color 0.3s ease;
          overflow-x: hidden;
        }

        /* Navbar */
        .navbar {
          position: sticky;
          top: 0;
          z-index: 50;
          background: ${t.navBg};
          backdrop-filter: blur(16px);
          border-bottom: 1px solid ${t.border};
        }
        .navbar-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 14px 24px;
          display: flex;
          flex-wrap: wrap; /* FIX: Prevents collision on small screens */
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        .logo-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .logo-img { height: 32px; width: 32px; object-fit: contain; }
        .logo-text-col { display: flex; flex-direction: column; }
        .logo-title { font-weight: 600; font-size: 1.1rem; line-height: 1.2; white-space: nowrap; }
        .logo-tag { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: ${t.textSec}; }
        
        .header-actions { display: flex; align-items: center; gap: 10px; }
        
        .theme-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 99px;
          border: 1px solid ${t.border};
          background: transparent; color: ${t.text};
          cursor: pointer; font-size: 0.75rem;
        }
        .login-btn {
          padding: 8px 20px; border-radius: 99px;
          background: ${t.accent}; color: white;
          border: none; font-weight: 500; font-size: 0.875rem;
          cursor: pointer; white-space: nowrap;
        }

        /* Main Content */
        .page-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 24px 80px;
        }

        /* Hero Grid */
        .landing-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 48px;
          align-items: center;
          margin-bottom: 60px;
        }

        /* Left Side */
        .badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 12px; border-radius: 99px;
          background: ${t.badgeBg}; border: 1px solid ${t.border};
          margin-bottom: 16px;
        }
        .badge-dot { width: 6px; height: 6px; background: #22c55e; border-radius: 50%; }
        .badge-text { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; color: ${t.badgeText}; }

        .hero-title {
          font-size: 3rem; font-weight: 800; line-height: 1.1; margin: 0 0 24px 0;
        }
        .highlight { color: ${t.accent}; }
        
        .hero-desc { font-size: 1.1rem; line-height: 1.6; color: ${t.textSec}; margin-bottom: 32px; max-width: 540px; }
        
        .cta-btn {
          padding: 14px 32px; border-radius: 99px;
          background: ${t.accent}; color: white;
          border: none; font-size: 1rem; font-weight: 600;
          cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
          box-shadow: 0 10px 25px -5px ${t.accent}66;
          transition: transform 0.2s;
        }
        .cta-btn:hover { transform: translateY(-2px); }

        .info-box {
          margin-top: 40px; padding: 20px;
          background: ${t.cardBg}; border: 1px solid ${t.border};
          border-radius: 12px;
        }
        .info-title { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700; color: ${t.badgeText}; margin-bottom: 8px; }
        .info-text { font-size: 0.95rem; color: ${t.textSec}; line-height: 1.5; }

        /* Right Side (Feature Card) */
        .feature-card {
          background: ${t.rightCardBg}; color: ${t.rightCardText};
          padding: 40px 32px; border-radius: 20px;
          box-shadow: 0 20px 40px -10px rgba(0,0,0,0.2);
        }
        .fc-header { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.15em; font-weight: 700; color: #64748b; margin-bottom: 8px; }
        .fc-title { font-size: 1.5rem; font-weight: 700; margin-bottom: 24px; line-height: 1.25; color: #0f172a; }
        
        .benefit-row { display: flex; gap: 12px; margin-bottom: 16px; align-items: flex-start; }
        .b-dot { width: 8px; height: 8px; background: ${t.accent}; border-radius: 50%; margin-top: 6px; flex-shrink: 0; }
        .b-text { font-size: 0.95rem; color: #334155; line-height: 1.5; }
        .b-bold { font-weight: 700; color: #0f172a; }

        .stats { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 32px; padding-top: 24px; border-top: 1px solid #e2e8f0; }
        .stat-box { padding: 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
        .stat-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.1em; color: #64748b; font-weight: 600; }
        .stat-val { font-size: 1.75rem; font-weight: 800; color: #0f172a; margin: 4px 0; }
        .stat-sub { font-size: 0.85rem; color: #64748b; }

        /* Flow Section */
        .flow-section {
          background: ${t.cardBg}; border: 1px solid ${t.border};
          border-radius: 20px; padding: 40px;
        }
        .flow-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; flex-wrap: wrap; gap: 16px; }
        .flow-h-title { font-size: 1.25rem; font-weight: 700; }
        .flow-pill { padding: 6px 12px; border-radius: 99px; border: 1px solid ${t.border}; font-size: 0.8rem; color: ${t.badgeText}; }

        .flow-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .step-card {
          background: ${t.flowStepBg}; border: 1px solid ${t.flowBorder};
          padding: 20px; border-radius: 16px; position: relative;
          transition: transform 0.2s;
        }
        .step-card:hover { transform: translateY(-4px); border-color: ${t.accent}; }
        .step-meta { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .step-idx { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: ${t.accent}; }
        .step-tag { font-size: 0.7rem; padding: 2px 8px; border-radius: 99px; border: 1px solid ${t.border}; color: ${t.textSec}; }
        .step-name { font-size: 1rem; font-weight: 700; color: ${t.flowTitle}; margin-bottom: 8px; }
        .step-desc { font-size: 0.85rem; color: ${t.textSec}; line-height: 1.5; }
        
        /* RESPONSIVE MEDIA QUERIES */
        
        /* Tablet & Large Zoom */
        @media (max-width: 1024px) {
          .landing-grid { grid-template-columns: 1fr; gap: 40px; }
          .hero-title { font-size: 2.5rem; }
          .flow-steps { grid-template-columns: 1fr 1fr; }
        }

        /* Mobile */
        @media (max-width: 640px) {
          .navbar-inner { padding: 12px 16px; }
          .header-actions { margin-top: 0; } /* Keep nicely aligned */
          
          .page-content { padding: 24px 16px; }
          
          .hero-title { font-size: 2rem; }
          .hero-desc { font-size: 1rem; }
          
          .feature-card { padding: 24px; }
          .fc-title { font-size: 1.25rem; }
          .stats { grid-template-columns: 1fr; }
          
          .flow-section { padding: 24px; }
          .flow-steps { grid-template-columns: 1fr; }
          
          .logo-text-col { display: none; } /* Hide text on very small screens to save space */
        }
      `}</style>

      <div className="landing-page">
        {/* Navbar */}
        <nav className="navbar">
          <div className="navbar-inner">
            <div className="logo-wrapper">
              <img src="/logo.png" alt="Logo" className="logo-img" />
              <div className="logo-text-col">
                <div className="logo-title">Prior Art Report Generator</div>
                <div className="logo-tag">Standardized report formatting</div>
              </div>
            </div>
            <div className="header-actions">
              <button className="theme-btn" onClick={toggleTheme}>
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
              </button>
              <button className="login-btn" onClick={onGetStarted}>
                Sign in
              </button>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="page-content">
          {/* Hero Section */}
          <div className="landing-grid">
            {/* Text Side */}
            <div className="hero-left">
              <div className="badge">
                <div className="badge-dot" />
                <span className="badge-text">Formatting & PDF Export</span>
              </div>
              <h1 className="hero-title">
                Turn prior art notes into{" "}
                <span className="highlight">clean, consistent reports</span>.
              </h1>
              <p className="hero-desc">
                A straightforward way to move from raw findings to a fully
                formatted prior art report using a standard, repeatable
                template. No more manual formatting headaches.
              </p>
              <button className="cta-btn" onClick={onGetStarted}>
                Create a report <span>→</span>
              </button>

              <div className="info-box">
                <div className="info-title">
                  Consistent structure, every time
                </div>
                <div className="info-text">
                  Use a single, shared template so every document follows the
                  same headings, order, and visual style. Reduce rework and keep
                  reviewers happy.
                </div>
              </div>
            </div>

            {/* Visual Side */}
            <div className="feature-card">
              <div className="fc-header">What the tool does</div>
              <div className="fc-title">
                Standardized formatting and one‑click PDF export
              </div>

              <div className="benefit-row">
                <div className="b-dot" />
                <div className="b-text">
                  <span className="b-bold">Template-based layout.</span> Capture
                  details in a predefined structure.
                </div>
              </div>
              <div className="benefit-row">
                <div className="b-dot" />
                <div className="b-text">
                  <span className="b-bold">Automatic styling.</span> Consistent
                  fonts and tables without manual tuning.
                </div>
              </div>
              <div className="benefit-row">
                <div className="b-dot" />
                <div className="b-text">
                  <span className="b-bold">Instant PDF export.</span> Generate
                  ready-to-share files in seconds.
                </div>
              </div>

              <div className="stats">
                <div className="stat-box">
                  <div className="stat-label">Formatting Time</div>
                  <div className="stat-val">Hours ↓</div>
                  <div className="stat-sub">Eliminate repetitive styling</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Consistency</div>
                  <div className="stat-val">100%</div>
                  <div className="stat-sub">Across all reports</div>
                </div>
              </div>
            </div>
          </div>

          {/* Flow Section */}
          <div className="flow-section">
            <div className="flow-head">
              <div className="flow-h-title">How it fits your workflow</div>
              <div className="flow-pill">4 Simple Steps</div>
            </div>

            <div className="flow-steps">
              {/* Step 1 */}
              <div className="step-card">
                <div className="step-meta">
                  <span className="step-idx">Step 1</span>
                  <span className="step-tag">Metadata</span>
                </div>
                <div className="step-name">Enter Details</div>
                <div className="step-desc">
                  Capture project name, filing details, and internal references
                  to build the header.
                </div>
              </div>

              {/* Step 2 */}
              <div className="step-card">
                <div className="step-meta">
                  <span className="step-idx">Step 2</span>
                  <span className="step-tag">Analysis</span>
                </div>
                <div className="step-name">Add Prior Art</div>
                <div className="step-desc">
                  Use structured fields for abstracts, claims, and images. No
                  formatting needed.
                </div>
              </div>

              {/* Step 3 */}
              <div className="step-card">
                <div className="step-meta">
                  <span className="step-idx">Step 3</span>
                  <span className="step-tag">Preview</span>
                </div>
                <div className="step-name">Review Report</div>
                <div className="step-desc">
                  See your content instantly laid out in the standard corporate
                  template.
                </div>
              </div>

              {/* Step 4 */}
              <div className="step-card">
                <div className="step-meta">
                  <span className="step-idx">Step 4</span>
                  <span className="step-tag">Output</span>
                </div>
                <div className="step-name">Download PDF</div>
                <div className="step-desc">
                  Export a polished PDF ready for filing, clients, or internal
                  archives.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LandingPage;
